import * as React from "react";
import { actionCreators as uiActionCreators } from "ui/store";
import { actionCreators as eventActionCreators } from "event/store";
import { actionCreators } from "page/calendar/store";
import { CalendarState, CustomImage, PostData } from "burde";
import moment from "moment";
import {
    NavigationComponent,
    LOCAL_STORAGE_RELOAD,
    LOCAL_STORAGE_RELOAD_CALENDAR_A5,
    LOCAL_STORAGE_RELOAD_CALENDAR_A6,
    LOCAL_STORAGE_RELOAD_NOTEBOOK_A5,
    LOCAL_STORAGE_RELOAD_NOTEBOOK_A6,
    LOCAL_STORAGE_CHECKOUT,
    LOCAL_STORAGE_CONFIGURATION,
    LOCAL_STORAGE_UPLOADED_COVER_IMAGES,
    LOCAL_STORAGE_UPLOADED_OWNER_PAGE_IMAGES,
    LOCAL_STORAGE_RELOAD_CALENDAR_B5,
    LOCAL_STORAGE_RELOAD_NOTEBOOK_B5,
} from "ui/constants";
import { withRouter, RouteComponentProps } from "react-router";
import { LocalStorageReloadItem } from "ui/constants";
import { CalendarSizes, CalendarType } from "svg/constants";
import { isEqual } from "lodash";
import { getComponentRoute } from "page/calendar/helper";

type LocalStorageHandlerProps = {
    calendar: CalendarState;
    loadFromLocalStorage: boolean;
    activeNavigationComponent: NavigationComponent;
    isAuthenticated: any;
    postData: PostData | null;
    uploadedCoverImages: Array<CustomImage>;
    uploadedOwnerPageImages: Array<CustomImage>;
} & typeof actionCreators &
    typeof uiActionCreators &
    typeof eventActionCreators;

interface LocalStorageHandlerState {}

class LocalStorageHandler extends React.Component<
    LocalStorageHandlerProps & RouteComponentProps,
    LocalStorageHandlerState
> {
    clearLocalStorage = () => {
        localStorage.removeItem(LOCAL_STORAGE_RELOAD);
        localStorage.removeItem(LOCAL_STORAGE_RELOAD_CALENDAR_A5);
        localStorage.removeItem(LOCAL_STORAGE_RELOAD_CALENDAR_A6);
        localStorage.removeItem(LOCAL_STORAGE_RELOAD_NOTEBOOK_A5);
        localStorage.removeItem(LOCAL_STORAGE_RELOAD_NOTEBOOK_A6);
        localStorage.removeItem(LOCAL_STORAGE_CONFIGURATION);
        localStorage.removeItem(LOCAL_STORAGE_UPLOADED_COVER_IMAGES);
        localStorage.removeItem(LOCAL_STORAGE_UPLOADED_OWNER_PAGE_IMAGES);
    };

    checkCalendar = () => {
        const {
            calendar: originalCalendar,
            activeNavigationComponent,
            isAuthenticated,
            uploadedCoverImages,
            uploadedOwnerPageImages,
        } = this.props;
        const timestamp = moment();

        const calendar: CalendarState = {
            ...originalCalendar,
        };

        if (!isAuthenticated) {
            calendar.personalDateItems = [];
        }

        let data: LocalStorageReloadItem = {
            timestamp,
            calendar,
            activeNavigationComponent,
        };

        this.storeArticleInfo(data);

        localStorage.setItem(
            LOCAL_STORAGE_UPLOADED_COVER_IMAGES,
            JSON.stringify(uploadedCoverImages),
        );
        localStorage.setItem(
            LOCAL_STORAGE_UPLOADED_OWNER_PAGE_IMAGES,
            JSON.stringify(uploadedOwnerPageImages),
        );
    };

    storeArticleInfo(data: LocalStorageReloadItem) {
        const calendarSize = data.calendar.calendarSize.toLowerCase();
        const calendarType = data.calendar.calendarType;

        if (calendarSize === CalendarSizes.A5.toLowerCase()) {
            if (calendarType === CalendarType.Calendar.toLowerCase()) {
                localStorage.setItem(
                    LOCAL_STORAGE_RELOAD_CALENDAR_A5,
                    JSON.stringify(data),
                );
            } else if (calendarType === CalendarType.Notebook.toLowerCase()) {
                localStorage.setItem(
                    LOCAL_STORAGE_RELOAD_NOTEBOOK_A5,
                    JSON.stringify(data),
                );
            }
        } else if (calendarSize === CalendarSizes.A6.toLowerCase()) {
            if (calendarType === CalendarType.Calendar.toLowerCase()) {
                localStorage.setItem(
                    LOCAL_STORAGE_RELOAD_CALENDAR_A6,
                    JSON.stringify(data),
                );
            } else if (calendarType === CalendarType.Notebook.toLowerCase()) {
                localStorage.setItem(
                    LOCAL_STORAGE_RELOAD_NOTEBOOK_A6,
                    JSON.stringify(data),
                );
            }
        } else if (calendarSize === CalendarSizes.B5.toLowerCase()) {
            if (calendarType === CalendarType.Calendar.toLowerCase()) {
                localStorage.setItem(
                    LOCAL_STORAGE_RELOAD_CALENDAR_B5,
                    JSON.stringify(data),
                );
            } else if (calendarType === CalendarType.Notebook.toLowerCase()) {
                localStorage.setItem(
                    LOCAL_STORAGE_RELOAD_NOTEBOOK_B5,
                    JSON.stringify(data),
                );
            }
        }
    }

    areCalendarKeysEqual = (articleSize: string, articleType: string) => {
        const localItem: any = this.getLocalItem(articleSize, articleType);

        const { calendar } = this.props;
        if (localItem) {
            const parsed: LocalStorageReloadItem = JSON.parse(localItem);

            if (!parsed.calendar) return false;

            const calendarKeys = Object.keys(calendar).sort();
            const localItemKeys = Object.keys(parsed.calendar).sort();

            return isEqual(calendarKeys, localItemKeys);
        } else {
            // If no local item exists, return true.
            return true;
        }
    };

    componentDidMount() {
        // Always remove checkout storage if we're not on the checkout page to avoid strange cases.
        localStorage.removeItem(LOCAL_STORAGE_CHECKOUT);

        const uploadedCoverImages = localStorage.getItem(
            LOCAL_STORAGE_UPLOADED_COVER_IMAGES,
        );

        if (uploadedCoverImages && uploadedCoverImages !== "undefined") {
            const parsedData = JSON.parse(uploadedCoverImages);

            if (Array.isArray(parsedData)) {
                this.props.updateUploadedCoverImages(parsedData);
            }
        }

        const uploadedOwnerPageImages = localStorage.getItem(
            LOCAL_STORAGE_UPLOADED_OWNER_PAGE_IMAGES,
        );

        if (
            uploadedOwnerPageImages &&
            uploadedOwnerPageImages !== "undefined"
        ) {
            const parsedData = JSON.parse(uploadedOwnerPageImages);

            if (Array.isArray(parsedData)) {
                this.props.updateUploadedOwnerPageImages(parsedData);
            }
        }

        const { loadFromLocalStorage, calendar, postData } = this.props;

        const checkCalendar = this.checkCalendar;

        const articleSize = calendar.calendarSize.toLowerCase();
        const articleType = calendar.calendarType;

        if (!this.areCalendarKeysEqual(articleSize, articleType)) {
            this.clearLocalStorage();
        }

        window.onunload = () => {
            checkCalendar();
        };

        const localItem: any = this.getLocalItem(articleSize, articleType);

        if (localItem) {
            const parsed: LocalStorageReloadItem = JSON.parse(localItem);

            if (loadFromLocalStorage) {
                const then = moment(parsed.timestamp);
                const now = moment(Date.now());

                const diffHours = moment.duration(now.diff(then)).asHours();

                if (diffHours < 3) {
                    // Don't set culture here.
                    parsed.calendar.culture = calendar.culture;

                    this.props.updateUserHasChangedBorder(true);

                    // Skip local storage for calendar entirely if there is postData (JH).
                    if (postData === null) {
                        this.props.setCalendarFromStorage(parsed.calendar);

                        if (parsed.activeNavigationComponent) {
                            this.props.UpdateActiveNavigationComponent(
                                parsed.activeNavigationComponent,
                            );

                            const targetComponentRoute = getComponentRoute(
                                parsed.activeNavigationComponent.route,
                                parsed.calendar.calendarSize,
                                parsed.calendar.calendarType,
                            );

                            if (
                                this.props.location.pathname !==
                                targetComponentRoute
                            ) {
                                this.props.history.push(targetComponentRoute);
                            }
                        }
                    }
                }
            }
        }

        this.props.UpdateLoadFromLocalStorage(false);
    }

    getLocalItem(articleSize: string, articleType: string) {
        if (articleSize === CalendarSizes.A5.toLowerCase()) {
            if (articleType === CalendarType.Calendar.toLowerCase()) {
                return localStorage.getItem(LOCAL_STORAGE_RELOAD_CALENDAR_A5);
            } else if (articleType === CalendarType.Notebook.toLowerCase()) {
                return localStorage.getItem(LOCAL_STORAGE_RELOAD_NOTEBOOK_A5);
            }
        } else if (articleSize === CalendarSizes.A6.toLowerCase()) {
            if (articleType === CalendarType.Calendar.toLowerCase()) {
                return localStorage.getItem(LOCAL_STORAGE_RELOAD_CALENDAR_A6);
            } else if (articleType === CalendarType.Notebook.toLowerCase()) {
                return localStorage.getItem(LOCAL_STORAGE_RELOAD_NOTEBOOK_A6);
            }
        } else if (articleSize === CalendarSizes.B5.toLowerCase()) {
            if (articleType === CalendarType.Calendar.toLowerCase()) {
                return localStorage.getItem(LOCAL_STORAGE_RELOAD_CALENDAR_B5);
            } else if (articleType === CalendarType.Notebook.toLowerCase()) {
                return localStorage.getItem(LOCAL_STORAGE_RELOAD_NOTEBOOK_B5);
            }
        }

        return localStorage.getItem(LOCAL_STORAGE_RELOAD);
    }

    getArticleSize(articleType: string = "calendar") {
        const path = window.location.pathname.toLowerCase();
        const sizeRegex = new RegExp(`(\/${articleType}\/)\\w+`); // checks the path for /url/{calendarSize}

        if (!sizeRegex.test(path)) {
            return false;
        }

        const regexInfo = sizeRegex.exec(path);

        if (!regexInfo) {
            return false;
        }

        const splitPath = regexInfo[0].split("/");
        const indexOfSize = splitPath.indexOf(articleType) + 1;

        if (indexOfSize === 0) {
            // If calendar not found, indexOfSize will be 0 (-1 + 1)
            return false;
        }

        return splitPath[indexOfSize];
    }

    render() {
        return !this.props.loadFromLocalStorage ? this.props.children : null;
    }
}

export default withRouter(LocalStorageHandler);
